/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  InputConfig,
  InputDescription,
} from "@smartsuite/automation-engine-api";
import { SelectEditorDescription_Type } from "@smartsuite/automation-engine-api";
import {
  KeyValueCustomSelect,
  MenuLineSeparator,
  Paragraph,
  Text,
  Tooltip,
} from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { CaptionBlock } from "../../../../../components/CaptionBlock";
import { useTheme } from "../../../../../stores";
import { useFieldMissingValidation } from "../../../../../stores/automation/hooks/field-missing-validation";
import { checkInputValidation } from "../../../../../stores/automation/utils/inputValidation";
import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../types/candidate";
import {
  DOCS_AUTOMATOR_AUTOMATION,
  INPUT_APPLICATION_ID,
  INPUT_SOLUTION_ID,
} from "../../../../../utils/constants";
import { getLocalCssColorVariables } from "../../../../../utils/cssVariablesUtils";
import { FieldMissingError } from "../../../Sections/ErrorSection";
import { ApplicationReferenceType } from "./ApplicationReferenceType";
import * as S from "./InputSelectStyles";
import {
  AUTOMATION_SELECT_EDITOR_DROPDOWN_CLASS,
  AutomationSelectEditorDropdownStyles,
} from "./InputSelectStyles";
import { ToggleType } from "./ToggleType";
import { WhiteToggleType } from "./WhiteToggleType";
import type { SelectOption } from "../../../../../domain/select-option";
import { getSVG } from "../../../../../utils/get-svg";
import ImageIcon from "./SupportedFeatures/ImageIcon";
import PdfIcon from "./SupportedFeatures/PdfIcon";
import { useRootStore } from "../../../../../stores/hooks";
import { Footer } from "./Footer";
import { ConnectionsAdminFooter } from "./Footer/ConnectionsAdminFooter";
import { RefreshingList } from "./SelectedOption/RefreshingList";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { selectOptionMatchesStoredValue } from "../../../../../stores/automation/utils/selectStoredSelectOption";
import {
  isSelectOptionDisabled,
  normalizeSelectOptionStatus,
} from "../../../../../stores/automation/utils/selectOptionStatus";
import { Colors } from "@smartsuite/types";
export const SelectEditor: React.FC<{
  description: InputDescription;
  config: InputConfig;
  onChange: (input: InputConfig) => void;
  disabled?: boolean;
  showValidationError?: boolean;
  trigger?: TriggerCandidate;
  action?: ActionCandidate;
}> = observer(
  ({
    description,
    config,
    onChange,
    disabled,
    showValidationError,
    trigger,
    action,
  }) => {
    const theme = useTheme();
    const store = useRootStore();
    const ref = useRef<HTMLDivElement>(null);
    const defaultApplyKeyRef = useRef<string | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const isRefreshing = store.automations.isRefreshingList;

    const validationError = useMemo(
      () =>
        showValidationError ? checkInputValidation(description, config) : "",
      [config, description, showValidationError],
    );

    let inputValue = { oneofKind: "string" as const, string: "" };

    if (config.value?.values[0]?.value.oneofKind === "string") {
      inputValue = config.value?.values[0]?.value;
    }

    const { defaultValue, options } = useMemo(() => {
      let defaultValue: string | undefined;

      let options: Array<{
        label: string;
        value: string;
        subLabel?: string;
        group?: string;
        status?: ReturnType<typeof normalizeSelectOptionStatus>;
      }> = [];
      if (description.editor?.editor.oneofKind === "select") {
        defaultValue = description.editor.editor.select.default;
        if (
          description.editor?.editor.select.type ===
          SelectEditorDescription_Type.DROPDOWN_WITH_DETAILS
        ) {
          options = description.editor?.editor.select.options.map((option) => {
            const optionValue = JSON.parse(option.value);
            const slug = optionValue.slug ?? optionValue.value ?? option.value;
            return {
              value: slug,
              label: option.label,
              subLabel: option.subLabel ?? optionValue.description,
              group: option.group ?? "",
              horizontalSeparator: option.horizontalSeparator ?? false,
              icon: option.icon,
              status: normalizeSelectOptionStatus(option.status),
            };
          });
        } else {
          options = description.editor?.editor.select.options.map((option) => ({
            value: option.value,
            label: option.label,
            subLabel: option.subLabel,
            group: option.group ?? "",
            horizontalSeparator: option.horizontalSeparator ?? false,
            icon: option.icon,
          }));
        }
      }
      if (description.editor?.editor.oneofKind === "status") {
        options = description.editor?.editor.status.options.map((option) => ({
          value: option.value,
          label: option.label,
        }));
      }
      if (description.editor?.editor.oneofKind === "tag") {
        options = description.editor?.editor.tag.options.map((option) => ({
          value: option.value,
          label: option.label,
        }));
      }

      return { defaultValue, options };
    }, [description]);

    const selectType =
      description.editor?.editor.oneofKind === "select"
        ? description.editor.editor.select.type
        : undefined;

    const findSelectedOption = (): (typeof options)[number] | undefined => {
      const direct = options.find((o) => o.value === inputValue.string);
      if (direct !== undefined) {
        return direct;
      }
      if (
        description.editor?.editor.oneofKind !== "select" ||
        selectType !== SelectEditorDescription_Type.DROPDOWN_WITH_DETAILS
      ) {
        return undefined;
      }
      const rawOption = description.editor.editor.select.options.find((opt) =>
        selectOptionMatchesStoredValue(
          opt.value,
          inputValue.string,
          selectType,
        ),
      );
      if (rawOption === undefined) {
        return undefined;
      }
      try {
        const parsed = JSON.parse(rawOption.value) as {
          slug?: string;
          value?: string;
        };
        const id = parsed.slug ?? parsed.value;
        return id !== undefined
          ? options.find((o) => o.value === id)
          : undefined;
      } catch {
        return options.find((o) => o.value === rawOption.value);
      }
    };

    const selectedOption = findSelectedOption();
    const disabledOptions = options.filter((option) =>
      isSelectOptionDisabled(option.status),
    );

    const onOpendChange = (e: boolean): void => {
      setOpen(e);
    };

    useEffect(() => {
      if (selectedOption?.value !== undefined || defaultValue === undefined) {
        defaultApplyKeyRef.current = null;
        return;
      }

      const defaultOption = options.find((o) => o.value === defaultValue);
      if (defaultOption === undefined) {
        return;
      }

      const applyKey = `${config.inputId}:${defaultValue}`;
      if (defaultApplyKeyRef.current === applyKey) {
        return;
      }
      defaultApplyKeyRef.current = applyKey;

      onChange({
        ...config,
        value: {
          values: [
            {
              value: {
                oneofKind: "string",
                string: String(defaultValue),
              },
            },
          ],
        },
      });
    }, [config, defaultValue, onChange, options, selectedOption?.value]);

    // we are reset the value to default if it's not present in the options
    useEffect(() => {
      const configValue = config.value?.values.length;

      if (configValue && !selectedOption?.value) {
        onChange({
          ...config,
          value: {
            values: [],
          },
        });
      }
    }, [options.map((o) => o.value).join(","), selectedOption?.value]);

    const showPlaceholder = (): string => {
      if (description.inputId === INPUT_APPLICATION_ID) {
        return t("automation-form.input-form-select-application");
      }
      if (description.inputId === INPUT_SOLUTION_ID) {
        return t("automation-form.input-form-select-solution");
      }
      if (description.inputId === "google-calendar-field-calendar-id") {
        return open
          ? t("automation-form.input-form-select-calendar")
          : "\u00a0";
      }
      return open ? t("automation-form.input-form-select-input") : "\u00a0";
    };

    const hasSelectError =
      validationError !== "" || !isInputValidByFieldMissing(config, action);

    const selectState = hasSelectError ? "error" : open ? "active" : "default";

    const classNames = cn({
      "container-is-open": open,
      "container-is-refreshing": isRefreshing,
      "is-error": hasSelectError,
    });

    const renderSelect = (type?: SelectEditorDescription_Type): ReactNode => {
      switch (type) {
        case SelectEditorDescription_Type.TOGGLE:
          return (
            <ToggleType options={options} config={config} onChange={onChange} />
          );
        case SelectEditorDescription_Type.WHITE_TOGGLE:
          return (
            <WhiteToggleType
              options={options}
              config={config}
              onChange={onChange}
            />
          );
        case SelectEditorDescription_Type.APPLICATION_TRIGGER_REFERENCE:
          return (
            <ApplicationReferenceType
              trigger={trigger}
              options={options}
              onChange={onChange}
              config={config}
            />
          );
        case SelectEditorDescription_Type.DROPDOWN_WITH_DETAILS:
          return (
            <KeyValueCustomSelect
              {...{ disabledOptions }}
              listItemsClassname={AUTOMATION_SELECT_EDITOR_DROPDOWN_CLASS}
              my="left top+2"
              state={selectState}
              disabled={disabled}
              value={selectedOption?.value}
              placeholder={showPlaceholder()}
              showClear={false}
              placeholderArrowStyle="filled-dark"
              options={options}
              renderOption={renderOptionWithDetails(description)}
              renderSelectedOption={renderSelectedOptionWithDetails(
                description,
              )}
              groupBy={(option) => option.group}
              className={classNames}
              renderGroupLabel={(label) => {
                if (label !== "") {
                  return (
                    <>
                      <MenuLineSeparator />
                      <Paragraph
                        className="select-list-items__label"
                        size="xs"
                        color="secondary"
                        weight="semibold"
                        ariaLabel="Select group label"
                      >
                        {label}
                      </Paragraph>
                    </>
                  );
                }
              }}
              portalStyles={getLocalCssColorVariables(theme)}
              onOpenedChange={onOpendChange}
              renderDropdownFooter={() => <ConnectionsAdminFooter />}
              onChange={(value) => {
                const picked = options.find((o) => o.value === value);
                if (
                  picked !== undefined &&
                  isSelectOptionDisabled(picked.status)
                ) {
                  return;
                }
                onChange({
                  ...config,
                  value: {
                    values: [
                      {
                        value: {
                          oneofKind: "string",
                          string: String(value),
                        },
                      },
                    ],
                  },
                });
              }}
            />
          );
        default:
          return (
            <KeyValueCustomSelect
              {...{ disabledOptions }}
              // @ts-expect-error
              actionsRef={ref}
              my="left top+2"
              state={selectState}
              disabled={disabled}
              value={selectedOption?.value}
              placeholder={showPlaceholder()}
              placeholderIcon={isRefreshing ? "refresh-thin" : undefined}
              showClear={false}
              emptyMessage={
                description.inputId === DOCS_AUTOMATOR_AUTOMATION
                  ? t("general.no-templates-docsautomator")
                  : undefined
              }
              placeholderArrowStyle="filled-dark"
              options={options}
              renderOption={renderOption}
              renderSelectedOption={renderSelectedOption}
              groupBy={(option) => option.group}
              className={classNames}
              renderGroupLabel={(label) => {
                if (label !== "") {
                  return (
                    <>
                      <MenuLineSeparator />
                      <Paragraph
                        className="select-list-items__label"
                        size="xs"
                        color="secondary"
                        weight="semibold"
                        ariaLabel="Select group label"
                      >
                        {label}
                      </Paragraph>
                    </>
                  );
                }
              }}
              renderDropdownFooter={() => (
                <Footer
                  // @ts-expect-error
                  handleClose={ref.current?.close}
                  config={
                    description.editor?.editor.oneofKind === "select"
                      ? description.editor?.editor.select?.footer
                      : undefined
                  }
                  action={action}
                />
              )}
              {...(isRefreshing
                ? {
                    renderPlaceholder: () => <RefreshingList />,
                  }
                : {})}
              portalStyles={getLocalCssColorVariables(theme)}
              onOpenedChange={onOpendChange}
              onChange={(value) => {
                const picked = options.find((o) => o.value === value);
                if (
                  picked !== undefined &&
                  isSelectOptionDisabled(picked.status)
                ) {
                  return;
                }
                onChange({
                  ...config,
                  value: {
                    values: [
                      {
                        value: {
                          oneofKind: "string",
                          string: String(value),
                        },
                      },
                    ],
                  },
                });
              }}
            />
          );
      }
    };

    return (
      <S.StyledReactSelectFieldContainer $hasError={hasSelectError}>
        <AutomationSelectEditorDropdownStyles />
        <CaptionBlock description={description} />

        {description.editor?.editor.oneofKind === "select" &&
          renderSelect(description.editor?.editor.select.type)}
        {validationError !== "" && (
          <Paragraph color={"error"} size={"xs"}>
            {validationError}
          </Paragraph>
        )}
        <FieldMissingError
          message={getFieldMissingErrorMessage(config, action)}
        />
      </S.StyledReactSelectFieldContainer>
    );
  },
);

const renderOption = (option: SelectOption): React.ReactNode => {
  const Icon = getSVG(option.icon);
  const unavailable = isSelectOptionDisabled(option.status);
  return (
    <S.Option
      $unavailable={unavailable}
      data-unavailable={unavailable ? "true" : undefined}
    >
      {Icon && <Icon />}
      <Text wrapMode="ellipsis" weight="medium" color="currentColor">
        {option.label}
      </Text>
    </S.Option>
  );
};

interface DropdownWithDetailsPayload {
  provider?: string;
  modelName?: string;
  webSearchEnabled?: boolean;
  description?: string;
}

const getDropdownWithDetailsPayload = (
  description: InputDescription,
  option: SelectOption,
): DropdownWithDetailsPayload => {
  if (description.editor?.editor.oneofKind !== "select") {
    return {};
  }
  const raw = description.editor.editor.select.options.find((o) =>
    selectOptionMatchesStoredValue(
      o.value,
      option.value,
      SelectEditorDescription_Type.DROPDOWN_WITH_DETAILS,
    ),
  )?.value;
  if (raw === undefined) {
    return {};
  }
  try {
    return JSON.parse(raw) as DropdownWithDetailsPayload;
  } catch {
    return {};
  }
};

const renderInternetConnection = (
  webSearchEnabled?: boolean,
): React.ReactNode =>
  webSearchEnabled === true ? (
    <span style={{ color: Colors.epbGreen }}>{t("caption.On")}</span>
  ) : (
    <span>{t("caption.Off")}</span>
  );

const renderConnectionDetailsLine = (
  payload: DropdownWithDetailsPayload,
): React.ReactNode | null => {
  const { provider, modelName, webSearchEnabled } = payload;
  if (provider === undefined && modelName === undefined) {
    return null;
  }
  return (
    <>
      {provider}
      {modelName !== undefined ? ` (${modelName})` : ""}
      {" • Internet connection: "}
      {renderInternetConnection(webSearchEnabled)}
    </>
  );
};

// If we will have more variations - we would need to refactor this function
// for now we are using it in ai action only
const renderOptionWithDetails =
  (description: InputDescription) =>
  (option: SelectOption): React.ReactNode => {
    const payload = getDropdownWithDetailsPayload(description, option);
    const detailsLine = renderConnectionDetailsLine(payload);
    const Icon = getSVG(option.icon);
    const unavailable = isSelectOptionDisabled(option.status);
    return (
      <S.Option
        $unavailable={unavailable}
        data-unavailable={unavailable ? "true" : undefined}
      >
        <S.ContentWrapper>
          <S.OptionMain>
            <S.IconWrapper>{Icon && <Icon />}</S.IconWrapper>
            <S.ContentColumn>
              <Text wrapMode="ellipsis" weight="medium" color="currentColor">
                {option.label} &nbsp;
              </Text>
              {detailsLine !== null && (
                <Text
                  wrapMode="ellipsis"
                  weight="regular"
                  color="inactive"
                  size="xs"
                  className="details-text"
                >
                  {detailsLine}
                </Text>
              )}
              {payload.description !== undefined &&
                payload.description !== "" && (
                  <Text
                    wrapMode="ellipsis"
                    weight="regular"
                    color="inactive"
                    size="xs"
                    className="description-text"
                  >
                    {payload.description}
                  </Text>
                )}
            </S.ContentColumn>
          </S.OptionMain>
          {unavailable && (
            <S.UnavailableBadge>{t("caption.Unavailable")}</S.UnavailableBadge>
          )}
        </S.ContentWrapper>
      </S.Option>
    );
  };

const renderSelectedOptionWithDetails =
  (description: InputDescription) =>
  (option: SelectOption): React.ReactNode => {
    const payload = getDropdownWithDetailsPayload(description, option);
    const detailsLine = renderConnectionDetailsLine(payload);
    const Icon = getSVG(option.icon);
    const unavailable = isSelectOptionDisabled(option.status);
    return (
      <S.SelectedOption
        $unavailable={unavailable}
        data-unavailable={unavailable ? "true" : undefined}
      >
        <S.ContentWrapper>
          <S.OptionMain>
            <S.IconWrapper>{Icon && <Icon />}</S.IconWrapper>
            <S.ContentColumn>
              <Text wrapMode="ellipsis" weight="medium" color="currentColor">
                {option.label}
              </Text>
              {detailsLine !== null && (
                <Text
                  wrapMode="ellipsis"
                  weight="regular"
                  color="inactive"
                  size="xs"
                  className="details-text"
                >
                  {detailsLine}
                </Text>
              )}
            </S.ContentColumn>
          </S.OptionMain>
        </S.ContentWrapper>
      </S.SelectedOption>
    );
  };

const renderSelectedOption = (option: SelectOption): React.ReactNode => {
  const Icon = getSVG(option.icon);
  const unavailable = isSelectOptionDisabled(option.status);
  return (
    <S.SelectedOption
      $unavailable={unavailable}
      data-unavailable={unavailable ? "true" : undefined}
    >
      {Icon && <Icon />}
      <Text wrapMode="ellipsis" weight="medium" color="currentColor">
        {option.label}
      </Text>
    </S.SelectedOption>
  );
};
